// Runtime config fetched once at app start from the backend. Holds values
// that differ between environments but aren't worth a rebuild: public
// uploads base URL today; future flags / CDN origins / feature toggles.
//
// Do NOT put secrets or auth values here — this endpoint is public.

export type RuntimeConfig = {
  uploadsPublicBase: string;
  // Server-reported deployment identity for the admin DEV pill: the
  // ENVIRONMENT label ("local" | "euw2-dev" | "euw2-prod" | "unset"), the
  // running commit sha, and the process start time (RFC3339 — uptime is
  // computed client-side so one fetch stays accurate). Display-only values:
  // nothing may branch on them (the pill's visibility is hostname-based,
  // lib/env.ts).
  environment: string;
  commit: string;
  startedAt: string;
};

const DEFAULT: RuntimeConfig = {
  // Safe fallback for the local dev fileserver (bind-mounted uploads/).
  uploadsPublicBase: "/uploads/public",
  environment: "",
  commit: "",
  startedAt: "",
};

let cached: RuntimeConfig | null = null;
let inflight: Promise<RuntimeConfig> | null = null;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch("/api/public-config", { credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      cached = {
        uploadsPublicBase:
          typeof data?.uploadsPublicBase === "string" && data.uploadsPublicBase
            ? data.uploadsPublicBase
            : DEFAULT.uploadsPublicBase,
        environment: typeof data?.environment === "string" ? data.environment : "",
        commit: typeof data?.commit === "string" ? data.commit : "",
        startedAt: typeof data?.startedAt === "string" ? data.startedAt : "",
      };
      return cached;
    } catch {
      cached = DEFAULT;
      return cached;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

// publicAssetURL turns a stored upload value into a renderable URL:
//   - empty / null → null
//   - already-absolute URL ("http(s)://…") → returned as-is (for pre-refactor
//     DB rows that still hold a full S3 URL; we don't write these any more,
//     but tolerate them so reading an old row doesn't throw)
//   - already-rooted path ("/…") → returned as-is (covers "/uploads/…" legacy
//     rows and "/api/..." internal routes)
//   - bare key ("tenants/acme/logos/x.png") → "<uploadsPublicBase>/<key>"
//
// Any stored value that doesn't fit one of those four shapes is a bug —
// the uploads refactor's invariant is that every new write lands under an
// owner-scoped prefix. In dev throw loudly so regressions are obvious;
// in production fall back to the public base rather than blanking the
// render, since a broken image is worse than a 404 the user can refresh.
//
// Synchronous: relies on runtime config being loaded at app start.
export function publicAssetURL(value: string | null | undefined): string | null {
  if (!value) return null;
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  if (value.startsWith("/")) return value;
  const base = (cached ?? DEFAULT).uploadsPublicBase.replace(/\/+$/, "");
  return `${base}/${value}`;
}
